/**
 * Friend Service
 *
 * Service for managing user friends including:
 * - Listing friends
 * - Listing pending friend requests
 * - Sending friend requests
 * - Accepting / declining friend requests
 * - Removing friends
 *
 * Extends the base CrudService for shared read/delete behavior
 * and uses direct apiClient calls for write operations where
 * the API returns only status codes.
 */

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';

import type { QueryParams } from '../types/api/query.types';
import type { ID } from '../types/common/id.types';
import type {
  FriendFeedEntry,
  FriendFeedMetadata,
  FriendFeedQuery,
  FriendFeedResult,
  FriendFeedSummary,
  FriendFeedSummaryMetadata,
  FriendFeedSummaryResult,
} from '../types/domain/friend-feed.types';
import type {
  BlockedFriend,
  Friend,
  FriendListMetadata,
  FriendListResult,
  FriendRequest,
  FriendRequestPayload,
  FriendRequestAction,
  FriendRequestActionPayload,
} from '../types/domain/friend.types';

/**
 * Falls back to an unlimited-looking envelope when the API omits one.
 *
 * `limit: null` is the safe default: it reads as "no ceiling to enforce", so a
 * malformed response hides the cap UI rather than inventing a number and
 * telling someone they are out of places.
 */
const FALLBACK_METADATA: FriendListMetadata = {
  count: 0,
  limit: null,
  plan: 'free',
};

/**
 * A feed envelope with no next page.
 *
 * `next_cursor: null` is the safe default: a malformed response stops the
 * reader rather than looping on a cursor the API never issued.
 */
const FALLBACK_FEED_METADATA: FriendFeedMetadata = {
  window_days: 0,
  friend_count: 0,
  next_cursor: null,
};

/**
 * A summary with nothing in it.
 *
 * `quiet` is the honest pulse rung for "we could not read anything", and an
 * empty grid renders as no rows rather than as a broken axis.
 */
const FALLBACK_FEED_SUMMARY: FriendFeedSummary = {
  pulse: { state: 'quiet', live: [], last_finished: null, trained_today: 0 },
  lede: null,
  week: {
    start: '',
    end: '',
    friends: [],
    you: { days: [], records: [], total: 0 },
  },
};

/**
 * Query object → wire params.
 *
 * `types` goes over as `types[]` because the API validates it as an array;
 * everything undefined is dropped so an omitted option takes the server's
 * default rather than sending `undefined` and being clamped to a minimum.
 */
function toFeedParams(query: FriendFeedQuery): QueryParams {
  const params: QueryParams = {};

  if (query.window !== undefined) params.window = query.window;
  if (query.limit !== undefined) params.limit = query.limit;
  if (query.before !== undefined) params.before = query.before;
  if (query.types?.length) params['types[]'] = query.types;

  return params;
}

// =============================================================================
// FRIEND SERVICE CLASS
// =============================================================================

export class FriendService extends CrudService<
  Friend,
  Friend,
  FriendRequestPayload
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/friend',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  // ---------------------------------------------------------------------------
  // READ OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get list of friends for the authenticated user.
   *
   * GET /api/friend
   */
  public async getFriends(params?: QueryParams): Promise<Friend[]> {
    return this.getAll(params);
  }

  /**
   * Get the friends list *with* its entitlement envelope.
   *
   * A direct `apiClient` read rather than `getAll()`, which unwraps the
   * response to a bare `data` array and drops `metadata` — taking the friend
   * cap with it. Anything rendering the cap has to come through here.
   *
   * Uncached on purpose: the count and the ceiling are the two values that
   * must be right the instant a friendship is added or removed, and this
   * service's cache is keyed per method rather than per resource.
   */
  public async getFriendsWithLimit(
    params?: QueryParams
  ): Promise<FriendListResult> {
    try {
      const response = await apiClient.get<{
        data?: Friend[];
        metadata?: FriendListMetadata;
      }>('/friend', { params });

      const body = response.data;

      return {
        friends: Array.isArray(body) ? body : (body?.data ?? []),
        metadata: body?.metadata ?? FALLBACK_METADATA,
      };
    } catch (error) {
      console.error('Failed to fetch friends:', error);
      rethrowTyped(error, 'Unable to fetch friends. Please try again.');
    }
  }

  /**
   * Get list of pending friend requests for the authenticated user.
   *
   * GET /api/friend/pending
   */
  public async getPendingFriends(
    params?: QueryParams
  ): Promise<FriendRequest[]> {
    try {
      return await this.withContext({
        template: '/friend/pending',
        cacheNamespace: 'pending-friends',
      }).getAll(params);
    } catch (error) {
      console.error('Failed to fetch pending friends:', error);
      rethrowTyped(error, 'Unable to fetch pending friends. Please try again.');
    }
  }

  /**
   * Requests this user has sent and nobody has answered yet.
   *
   * GET /api/friend/sent
   *
   * Withdrawing one is `removeFriend(id)` — the API resolves a delete on a
   * pending row to a withdrawal, so there is no separate rescind call.
   */
  public async getSentRequests(params?: QueryParams): Promise<FriendRequest[]> {
    try {
      return await this.withContext({
        template: '/friend/sent',
        cacheNamespace: 'sent-friend-requests',
      }).getAll(params);
    } catch (error) {
      console.error('Failed to fetch sent friend requests:', error);
      rethrowTyped(
        error,
        'Unable to fetch sent friend requests. Please try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // WRITE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Send a friend request to another user.
   *
   * POST /api/friend
   *
   * The API returns 201 status on success and no response body
   * is required by the frontend.
   */
  public async sendFriendRequest(data: FriendRequestPayload): Promise<void> {
    if (!data?.username) {
      throw new Error('Friend username is required to send a friend request.');
    }

    try {
      await this.create(data);

      // The new row lands in the *sent* inbox, which caches under its own
      // namespace and would otherwise not show the request just sent.
      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error('Failed to send friend request:', error);
      // `rethrowTyped` re-throws an ApiErrorClass by identity, so the API's
      // own 409 copy ("You already have a pending request with this person.")
      // survives for the caller to render. The fallback only covers an
      // untyped throw.
      rethrowTyped(error, 'Unable to send friend request. Please try again.');
    }
  }

  /**
   * Accept or decline a pending friend request.
   *
   * PUT/PATCH /api/friend/{friend}
   *
   * The API returns 200 status on success. We treat this as a
   * side-effect-only operation and do not depend on the response body.
   */
  public async respondToFriendRequest(
    id: ID,
    action: FriendRequestAction
  ): Promise<void> {
    if (!id) {
      throw new Error('Friend ID is required to update friend request.');
    }

    if (!action) {
      throw new Error('Action is required to update friend request.');
    }

    try {
      const payload: FriendRequestActionPayload = { action };
      const response = await apiClient.patch(`/friend/${id}`, payload);

      if (response.status !== 200) {
        throw new Error(`Failed to update friend with ID ${id}`);
      }

      // Contextless, so this widens across namespaces — the pending list
      // caches under `pending-friends` and would otherwise be missed.
      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error(`Failed to update friend request ${id}:`, error);
      rethrowTyped(error, 'Unable to update friend request. Please try again.');
    }
  }

  /**
   * Remove (unfriend) an existing friend, or withdraw a request you sent.
   *
   * DELETE /api/friend/{friend}
   *
   * One endpoint covers both: the API does not guard the delete on status, so
   * on an accepted row this ends the friendship and on a pending one it
   * withdraws the request. There is no `rescind` action.
   *
   * The API returns 204 on success, which matches CrudService.delete.
   */
  public async removeFriend(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Friend ID is required to remove friend.');
    }

    try {
      await this.delete(id);

      // Widen past the default namespace: withdrawing hits the sent inbox,
      // and unfriending moves the count the cap is measured against.
      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error(`Failed to remove friend ${id}:`, error);
      rethrowTyped(error, 'Unable to remove friend. Please try again.');
    }
  }

  /**
   * Block someone, ending the friendship and stopping them requesting again.
   *
   * POST /api/block/friend/{username}
   *
   * Addressed by **username**, not the pivot hashid — the endpoint is shared
   * with client blocking and keys on the user, not the relationship.
   */
  public async blockFriend(username: string): Promise<void> {
    if (!username) {
      throw new Error('Username is required to block a friend.');
    }

    try {
      await apiClient.post(`/block/friend/${username}`);
      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error(`Failed to block ${username}:`, error);
      rethrowTyped(error, 'Unable to block this person. Please try again.');
    }
  }

  /**
   * Lift a block you placed.
   *
   * DELETE /api/unblock/friend/{username}
   *
   * This does **not** restore the friendship — the API deletes the row, so
   * both parties become strangers and someone must request again. Only the
   * person who placed the block can lift it, and the response is 204 either
   * way so a non-blocker cannot learn that a block exists.
   */
  public async unblockFriend(username: string): Promise<void> {
    if (!username) {
      throw new Error('Username is required to unblock a friend.');
    }

    try {
      await apiClient.delete(`/unblock/friend/${username}`);
      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error(`Failed to unblock ${username}:`, error);
      rethrowTyped(error, 'Unable to unblock this person. Please try again.');
    }
  }

  /**
   * The people this user has blocked.
   *
   * GET /api/blocklist/friend
   */
  public async getBlockedFriends(): Promise<BlockedFriend[]> {
    try {
      const response = await apiClient.get<
        BlockedFriend[] | { data: BlockedFriend[] }
      >('/blocklist/friend');
      const body = response.data;

      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      console.error('Failed to fetch blocked friends:', error);
      rethrowTyped(error, 'Unable to fetch blocked people. Please try again.');
    }
  }

  // ===========================================================================
  // FEED
  // ===========================================================================

  /**
   * One page of the friend feed's chronological record.
   *
   * GET /api/friend/feed
   *
   * A bespoke read rather than `CrudService.getAll()` for the same reason
   * {@link getFriendsWithLimit} is: the cursor lives in the response
   * `metadata`, and `getAll()` unwraps to the bare `data` array. Without the
   * cursor there is no second page.
   */
  public async getFeed(query: FriendFeedQuery = {}): Promise<FriendFeedResult> {
    try {
      const response = await apiClient.get<{
        data?: FriendFeedEntry[];
        metadata?: FriendFeedMetadata;
      }>('/friend/feed', { params: toFeedParams(query) });

      const body = response.data;

      return {
        entries: Array.isArray(body) ? body : (body?.data ?? []),
        metadata: body?.metadata ?? FALLBACK_FEED_METADATA,
      };
    } catch (error) {
      console.error('Failed to fetch the friend feed:', error);
      rethrowTyped(error, 'Unable to load the feed. Please try again.');
    }
  }

  /**
   * The pulse band, the lede and the week grid, in one call.
   *
   * GET /api/friend/feed/summary
   *
   * One request rather than three: all three aggregates come off the same
   * friend list and the same privacy resolution, and none of them re-fetches
   * while the record pages beneath them.
   */
  public async getFeedSummary(
    query: Pick<FriendFeedQuery, 'window'> = {}
  ): Promise<FriendFeedSummaryResult> {
    try {
      const response = await apiClient.get<{
        data?: FriendFeedSummary;
        metadata?: FriendFeedSummaryMetadata;
      }>('/friend/feed/summary', { params: toFeedParams(query) });

      const body = response.data;

      return {
        summary: body?.data ?? FALLBACK_FEED_SUMMARY,
        metadata: body?.metadata ?? { friend_count: 0, friends_trained: 0 },
      };
    } catch (error) {
      console.error('Failed to fetch the friend feed summary:', error);
      rethrowTyped(error, 'Unable to load the feed. Please try again.');
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default friend service instance.
 */
export const friendService = new FriendService();
